import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFetch } from "@/hooks/useFetch";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ErrorState, Loading, PageHeader, Spinner } from "@/components/States";
import PlanCards, { IntervalToggle } from "@/components/billing/PlanCards";

function CurrentPlan({ ws, onPortal, busy }) {
  const s = ws.state, sub = ws.subscription || {};
  const label = { trialing: "Free trial", active: "Active", past_due: "Payment failed", expired: "Trial ended", canceled: "Canceled" }[s.status];
  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border bg-card p-6" data-testid="current-plan">
      <div>
        <p className="eyebrow">Current plan</p>
        <div className="mt-2 flex items-center gap-2 section-title leading-none" data-testid="current-plan-name">{s.limits.name} <Badge variant={s.active ? "secondary" : "destructive"} data-testid="current-plan-status">{label}</Badge></div>
        <p className="mt-2 text-sm text-muted-foreground" data-testid="current-plan-detail">
          {s.status === "trialing" && `${s.trial_days_left} days left. Pick a plan any time; you won't be charged until the trial ends.`}
          {s.status === "active" && sub.complimentary && "Complimentary workspace."}
          {s.status === "active" && !sub.complimentary && `Billed ${sub.interval || "monthly"}${sub.current_period_end ? `, renews ${sub.current_period_end.slice(0, 10)}` : ""}${sub.cancel_at_period_end ? " (cancels at period end)" : ""}.`}
          {s.status === "past_due" && "Update your payment method to keep your workspace active."}
          {(s.status === "expired" || s.status === "canceled") && "Editing is paused until you choose a plan. Your data is safe."}
        </p>
      </div>
      {sub.stripe_customer_id && <Button variant="outline" className="rounded-full" onClick={onPortal} disabled={busy} data-testid="manage-billing-button">{busy ? <Spinner /> : <ExternalLink className="mr-2 h-4 w-4" />}Manage billing</Button>}
    </div>
  );
}

export default function Billing({ embedded = false }) {
  const { workspace, setWorkspace } = useAuth();
  const plans = useFetch("/billing/plans");
  const [interval, setInterval] = useState("monthly");
  const [busy, setBusy] = useState(null);

  useEffect(() => { api.post("/billing/sync").then((r) => setWorkspace(r.data)).catch(() => {}); }, [setWorkspace]);

  const choose = async (plan) => {
    setBusy(plan);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key: `${plan}_${interval}`, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };
  const portal = async () => {
    setBusy("portal");
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(errMsg(e)); setBusy(null); }
  };

  return (
    <div className={embedded ? "space-y-8" : "max-w-6xl space-y-8 p-4 md:p-8"}>
      {embedded ? <div className="flex justify-end"><IntervalToggle value={interval} onChange={setInterval} /></div> : (
        <PageHeader eyebrow="Subscription" title="Billing">
          <IntervalToggle value={interval} onChange={setInterval} />
        </PageHeader>
      )}
      {workspace?.kind === "owner_builder" && <div className="rounded-xl border bg-secondary p-5 text-sm" data-testid="owner-builder-billing">Owner-Builder accounts are free while E.L.M is in preview. Pricing is coming soon and you will hear from us well before anything changes.</div>}
      {workspace && workspace.kind !== "owner_builder" && <CurrentPlan ws={workspace} onPortal={portal} busy={busy === "portal"} />}
      {plans.loading ? <Loading rows={2} /> : plans.error ? <ErrorState message={plans.error} onRetry={plans.reload} /> : (
        <PlanCards plans={plans.data} interval={interval} current={workspace?.state.status === "active" ? workspace.plan : null} onChoose={choose} busyKey={busy !== "portal" ? busy : null} />
      )}
      <p className="text-xs text-muted-foreground">Prices in Canadian dollars. Taxes are calculated at checkout. Change or cancel any time from Manage billing.</p>
    </div>
  );
}
